import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { auth } from '../firebase';
import { env } from '../config/environment';
import userTeamService from '../services/userTeamService';
import { normalizePhoneE164, formatPhoneDisplay } from '../utils/smsUtils';
import { getClientDisplayName } from '../utils/taskUtils';
import { finalizeActiveCallLog } from '../utils/voiceCallFinalize';
import { fetchActiveVoiceSession } from '../utils/voiceCallSession';
import {
  ensureCommsNotificationPermission,
  notifyCallEnded,
  notifyIncomingCall,
} from '../utils/commsNotifications';

const VoiceCallContext = createContext(null);

/** How long the header shows "Call ended" before clearing. */
const CALL_ENDED_NOTICE_MS = 5000;
const ACTIVE_CALL_STATES = new Set(['connecting', 'connected', 'incoming']);

let sharedDevice = null;
let sharedDeviceIdentity = null;
let incomingCallHandler = null;

export function setIncomingCallHandler(handler) {
  incomingCallHandler = handler;
}

async function fetchIncomingSessionMeta() {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) return null;

  const response = await fetch(env.apiEndpoints.voiceIncomingSession, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });
  if (!response.ok) return null;
  const result = await response.json();
  return result?.active ? result : null;
}

async function fetchVoiceToken() {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not authenticated');

  const response = await fetch(env.apiEndpoints.voiceToken, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({}),
  });
  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.error || 'Voice calling is not available');
  }
  return result;
}

// The Twilio SDK (~170 KB) loads only for users who can call, after the app is up.
let twilioDeviceClass = null;
async function loadTwilioDevice() {
  if (!twilioDeviceClass) {
    ({ Device: twilioDeviceClass } = await import('@twilio/voice-sdk'));
  }
  return twilioDeviceClass;
}

async function ensureVoiceDevice(identity) {
  if (sharedDevice && sharedDeviceIdentity === identity) {
    const Device = await loadTwilioDevice();
    if (sharedDevice.state === Device.State.Unregistered) {
      await sharedDevice.register();
    }
    return sharedDevice;
  }

  if (sharedDevice) {
    try {
      sharedDevice.destroy();
    } catch {
      // ignore teardown errors when switching users
    }
    sharedDevice = null;
    sharedDeviceIdentity = null;
  }

  const { token } = await fetchVoiceToken();
  const Device = await loadTwilioDevice();
  const device = new Device(token, {
    logLevel: 'warn',
    codecPreferences: ['opus', 'pcmu'],
  });

  device.on('tokenWillExpire', async () => {
    try {
      const refreshed = await fetchVoiceToken();
      await device.updateToken(refreshed.token);
    } catch (err) {
      console.error('Twilio Voice token refresh failed:', err);
    }
  });

  device.on('error', (err) => {
    console.error('Twilio Voice device error:', err);
  });

  device.on('incoming', (call) => {
    if (typeof incomingCallHandler === 'function') {
      incomingCallHandler(call);
      return;
    }
    try {
      call.reject();
    } catch {
      // ignore
    }
  });

  await device.register();
  sharedDevice = device;
  sharedDeviceIdentity = identity;
  return device;
}

function resetCallSession(
  setCallState,
  setActiveCallClient,
  setCallDirection,
  setIsMuted,
  activeCallRef
) {
  activeCallRef.current = null;
  setCallState('idle');
  setActiveCallClient(null);
  setCallDirection(null);
  setIsMuted(false);
}

function attachCallListeners({
  call,
  setCallState,
  setIsMuted,
  activeCallRef,
  onAccept,
  onReject,
  beginCallEndedNotice,
  outboundAwaitClientAnswer = false,
}) {
  const endCall = () => {
    beginCallEndedNotice();
  };

  call.on('mute', (muted) => {
    setIsMuted(Boolean(muted));
  });

  call.on('accept', () => {
    if (!outboundAwaitClientAnswer) {
      setCallState('connected');
    }
    onAccept?.();
  });

  call.on('disconnect', endCall);
  call.on('cancel', () => {
    endCall();
    onReject?.(new Error('Call was cancelled.'));
  });
  call.on('reject', () => {
    endCall();
    onReject?.(new Error('Call was rejected.'));
  });
  call.on('error', (err) => {
    endCall();
    onReject?.(err instanceof Error ? err : new Error('Call failed.'));
  });
}

function parseIncomingCallMeta(call) {
  const params = call?.customParameters || new Map();
  const getParam = (key) => {
    if (params instanceof Map) return params.get(key) || '';
    return params?.[key] || '';
  };

  const clientId = String(getParam('clientId') || '').trim();
  const direction = String(getParam('direction') || 'inbound').trim().toLowerCase();
  const callerPhone = normalizePhoneE164(getParam('callerPhone') || getParam('From') || call?.parameters?.From);
  const clientName = String(getParam('clientName') || '').trim()
    || (callerPhone ? `Caller ${formatPhoneDisplay(callerPhone)}` : 'Incoming call');

  return {
    clientId: clientId || null,
    clientName,
    callerPhone,
    direction: direction === 'outbound' ? 'outbound' : 'inbound',
  };
}

export function VoiceCallProvider({ user, children }) {
  const [callState, setCallState] = useState('idle');
  const [activeCallClient, setActiveCallClient] = useState(null);
  const [callEndedNotice, setCallEndedNotice] = useState(null);
  const [callDirection, setCallDirection] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const activeCallRef = useRef(null);
  const activeCallClientRef = useRef(null);
  const callStateRef = useRef('idle');
  const callDirectionRef = useRef(null);
  const callEndedTimerRef = useRef(null);
  const beginCallEndedNoticeRef = useRef(() => {});

  useEffect(() => {
    activeCallClientRef.current = activeCallClient;
  }, [activeCallClient]);

  const requestCallLogFinalize = useCallback(() => {
    const client = activeCallClientRef.current;
    finalizeActiveCallLog({
      clientId: client?.id || null,
      callLogId: client?.callLogId || null,
    }).catch((err) => {
      console.warn('Call log finalize failed:', err);
    });
  }, []);

  const clearCallEndedTimer = useCallback(() => {
    if (callEndedTimerRef.current) {
      clearTimeout(callEndedTimerRef.current);
      callEndedTimerRef.current = null;
    }
  }, []);

  const beginCallEndedNotice = useCallback(() => {
    if (callStateRef.current === 'ended') return;

    const client = activeCallClientRef.current;
    activeCallRef.current = null;
    requestCallLogFinalize();

    if (!client) {
      clearCallEndedTimer();
      setCallEndedNotice(null);
      resetCallSession(
        setCallState,
        setActiveCallClient,
        setCallDirection,
        setIsMuted,
        activeCallRef
      );
      return;
    }

    setCallEndedNotice({
      id: client.id,
      name: client.name,
      phone: client.phone,
    });
    setCallState('ended');
    notifyCallEnded({ clientName: client.name });

    clearCallEndedTimer();
    callEndedTimerRef.current = setTimeout(() => {
      callEndedTimerRef.current = null;
      setCallEndedNotice(null);
      resetCallSession(
        setCallState,
        setActiveCallClient,
        setCallDirection,
        setIsMuted,
        activeCallRef
      );
    }, CALL_ENDED_NOTICE_MS);
  }, [clearCallEndedTimer, requestCallLogFinalize]);

  useEffect(() => {
    beginCallEndedNoticeRef.current = beginCallEndedNotice;
  }, [beginCallEndedNotice]);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  useEffect(() => {
    callDirectionRef.current = callDirection;
  }, [callDirection]);

  const smsConfig = useMemo(
    () => userTeamService.getUserSmsConfig(user?.email),
    [user?.email]
  );
  const voiceAvailable = Boolean(user?.email && smsConfig.smsEnabled && smsConfig.smsNumber);

  useEffect(() => () => {
    clearCallEndedTimer();
    if (activeCallRef.current) {
      try {
        activeCallRef.current.disconnect();
      } catch {
        // ignore
      }
    }
    sharedDevice = null;
    sharedDeviceIdentity = null;
    incomingCallHandler = null;
  }, [clearCallEndedTimer]);

  useEffect(() => {
    if (!voiceAvailable || !user?.email) return undefined;
    if (!ACTIVE_CALL_STATES.has(callState) || callState === 'incoming') {
      return undefined;
    }

    let cancelled = false;
    const endCallFromRemote = () => {
      const call = activeCallRef.current;
      if (call) {
        try {
          call.disconnect();
        } catch {
          beginCallEndedNoticeRef.current();
        }
      } else {
        beginCallEndedNoticeRef.current();
      }
    };

    const poll = async () => {
      if (cancelled) return;
      const state = callStateRef.current;
      const direction = callDirectionRef.current;

      if (state === 'connecting' && direction === 'outbound') {
        const remote = await fetchActiveVoiceSession().catch(() => ({ active: false }));
        if (cancelled) return;
        if (remote?.callLogId || remote?.clientId) {
          setActiveCallClient((prev) => {
            if (!prev) return prev;
            const nextId = remote.clientId || prev.id || null;
            const nextLogId = remote.callLogId || prev.callLogId || null;
            const nextName = remote.clientName || prev.name;
            if (prev.id === nextId && prev.callLogId === nextLogId && prev.name === nextName) {
              return prev;
            }
            return {
              ...prev,
              id: nextId,
              callLogId: nextLogId,
              name: nextName,
              phone: remote.clientPhone || prev.phone,
            };
          });
        }
        if (remote?.clientAnswered) {
          setCallState('connected');
        }
        return;
      }

      if (state !== 'connected') return;

      const remote = await fetchActiveVoiceSession().catch(() => ({ active: false }));
      if (cancelled || remote?.active) return;
      if (callStateRef.current !== 'connected') return;
      endCallFromRemote();
    };

    poll();
    const intervalMs = callState === 'connecting' && callDirection === 'outbound' ? 1000 : 2000;
    const intervalId = setInterval(poll, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [voiceAvailable, user?.email, callState, callDirection]);

  useEffect(() => {
    if (!voiceAvailable || !user?.email) {
      setIncomingCallHandler(null);
      return undefined;
    }

    const identity = user.email.trim().toLowerCase();

    setIncomingCallHandler(async (call) => {
      const state = callStateRef.current;
      if (activeCallRef.current || (state !== 'idle' && state !== 'ended')) {
        try {
          call.reject();
        } catch {
          // ignore
        }
        return;
      }

      const meta = parseIncomingCallMeta(call);
      const sessionMeta = await fetchIncomingSessionMeta().catch(() => null);
      const clientId = sessionMeta?.clientId || meta.clientId;
      const clientName = sessionMeta?.clientName || meta.clientName;
      const callerPhone = sessionMeta?.clientPhone || meta.callerPhone;

      activeCallRef.current = call;
      setCallDirection('inbound');
      setActiveCallClient({
        id: clientId,
        name: clientName,
        phone: callerPhone || null,
      });
      setIsMuted(false);
      setCallState('incoming');
      notifyIncomingCall({
        clientName,
        callerPhone: callerPhone ? formatPhoneDisplay(callerPhone) : null,
      });

      attachCallListeners({
        call,
        setCallState,
        setIsMuted,
        activeCallRef,
        beginCallEndedNotice,
      });
    });

    ensureVoiceDevice(identity)
      .then(() => ensureCommsNotificationPermission().catch(() => {}))
      .catch((err) => {
        console.error('Twilio Voice device registration failed:', err);
      });

    return () => {
      setIncomingCallHandler(null);
    };
  }, [voiceAvailable, user?.email, beginCallEndedNotice]);

  const acceptIncoming = useCallback(() => {
    const call = activeCallRef.current;
    if (!call || callStateRef.current !== 'incoming') return;

    try {
      setCallState('connecting');
      call.accept();
    } catch (err) {
      resetCallSession(setCallState, setActiveCallClient, setCallDirection, setIsMuted, activeCallRef);
      throw err instanceof Error ? err : new Error('Failed to accept call.');
    }
  }, []);

  const rejectIncoming = useCallback(() => {
    const call = activeCallRef.current;
    if (!call || callStateRef.current !== 'incoming') return;

    try {
      call.reject();
    } catch {
      // ignore
    }
    resetCallSession(setCallState, setActiveCallClient, setCallDirection, setIsMuted, activeCallRef);
  }, []);

  const startOutboundCall = useCallback(async ({
    connectParams,
    displayClient,
  }) => {
    if (!voiceAvailable) {
      throw new Error('Voice calling is not enabled for your account.');
    }
    if (callStateRef.current !== 'idle' && callStateRef.current !== 'ended') {
      throw new Error('A call is already in progress.');
    }

    ensureCommsNotificationPermission().catch(() => {});

    setActiveCallClient(displayClient);
    setCallDirection('outbound');
    setIsMuted(false);
    setCallState('connecting');

    try {
      const identity = user?.email?.trim().toLowerCase();
      const device = await ensureVoiceDevice(identity);
      const call = await device.connect({
        params: connectParams,
      });

      activeCallRef.current = call;

      return await new Promise((resolve, reject) => {
        let settled = false;
        const finish = (fn, value) => {
          if (settled) return;
          settled = true;
          fn(value);
        };

        attachCallListeners({
          call,
          setCallState,
          setIsMuted,
          activeCallRef,
          outboundAwaitClientAnswer: true,
          onAccept: () => finish(resolve, {
            clientName: displayClient.name,
            clientPhone: displayClient.phone,
          }),
          onReject: (err) => finish(reject, err),
          beginCallEndedNotice,
        });
      });
    } catch (err) {
      resetCallSession(setCallState, setActiveCallClient, setCallDirection, setIsMuted, activeCallRef);
      throw err;
    }
  }, [voiceAvailable, user?.email, beginCallEndedNotice]);

  const callClient = useCallback(async (client) => {
    if (!client?.id) {
      throw new Error('Client not found.');
    }
    const clientPhone = normalizePhoneE164(client?.personal?.contactNumber);
    if (!clientPhone) {
      throw new Error('Add a valid mobile number in Fact Find → Personal Information.');
    }

    const clientName = getClientDisplayName(client);
    return startOutboundCall({
      connectParams: { clientId: client.id },
      displayClient: { id: client.id, name: clientName, phone: clientPhone, callLogId: null },
    });
  }, [startOutboundCall]);

  const callNumber = useCallback(async (rawNumber) => {
    const phone = normalizePhoneE164(rawNumber);
    if (!phone) {
      throw new Error('Enter a valid Australian mobile number.');
    }

    const displayName = formatPhoneDisplay(phone);
    return startOutboundCall({
      connectParams: { To: phone },
      displayClient: {
        id: null,
        name: displayName,
        phone,
        callLogId: null,
        unlinked: true,
      },
    });
  }, [startOutboundCall]);

  const hangUp = useCallback(() => {
    if (activeCallRef.current) {
      try {
        activeCallRef.current.disconnect();
      } catch {
        beginCallEndedNotice();
      }
      return;
    }
    beginCallEndedNotice();
  }, [beginCallEndedNotice]);

  const setMute = useCallback((muted) => {
    const call = activeCallRef.current;
    const state = callStateRef.current;
    if (!call || state === 'idle' || state === 'connecting' || state === 'incoming') return;

    call.mute(Boolean(muted));
    setIsMuted(Boolean(muted));
  }, []);

  const value = useMemo(
    () => ({
      voiceAvailable,
      callState,
      callDirection,
      activeCallClient,
      callEndedNotice,
      isMuted,
      isIncomingCall: callState === 'incoming',
      isDialling: callState === 'connecting' && callDirection === 'outbound',
      isCallEndedNotice: callState === 'ended' && Boolean(callEndedNotice),
      isCallBusy: callState !== 'idle' && callState !== 'ended',
      isCallActive: ACTIVE_CALL_STATES.has(callState) && Boolean(activeCallClient),
      callClient,
      callNumber,
      acceptIncoming,
      rejectIncoming,
      hangUp,
      setMute,
      ensureCommsNotificationPermission,
      ensureVoiceNotificationPermission: ensureCommsNotificationPermission,
      getCommsNotificationPermission: () => {
        if (typeof Notification === 'undefined') return 'unsupported';
        return Notification.permission;
      },
      getVoiceNotificationPermission: () => {
        if (typeof Notification === 'undefined') return 'unsupported';
        return Notification.permission;
      },
    }),
    [
      voiceAvailable,
      callState,
      callDirection,
      activeCallClient,
      callEndedNotice,
      isMuted,
      callClient,
      callNumber,
      acceptIncoming,
      rejectIncoming,
      hangUp,
      setMute,
    ]
  );

  return (
    <VoiceCallContext.Provider value={value}>
      {children}
    </VoiceCallContext.Provider>
  );
}

export function useVoiceCall() {
  const ctx = useContext(VoiceCallContext);
  if (!ctx) {
    return {
      voiceAvailable: false,
      callState: 'idle',
      callDirection: null,
      activeCallClient: null,
      isMuted: false,
      isIncomingCall: false,
      isDialling: false,
      isCallBusy: false,
      isCallActive: false,
      isCallEndedNotice: false,
      callEndedNotice: null,
      ensureCommsNotificationPermission: async () => 'unsupported',
      ensureVoiceNotificationPermission: async () => 'unsupported',
      getCommsNotificationPermission: () => 'unsupported',
      getVoiceNotificationPermission: () => 'unsupported',
      callClient: async () => {
        throw new Error('Voice calling is not available.');
      },
      callNumber: async () => {
        throw new Error('Voice calling is not available.');
      },
      acceptIncoming: () => {},
      rejectIncoming: () => {},
      hangUp: () => {},
      setMute: () => {},
    };
  }
  return ctx;
}

/** @deprecated Use useVoiceCall — kept for existing imports. */
export const useTwilioVoice = useVoiceCall;
